<template>
  <div class="similar-music-page" v-loading="loading">
    <aside
      class="similar-music-page__sidebar"
      :class="{ 'is-collapsed': sidebarCollapsed }"
    >
      <div class="similar-music-page__sidebar-header">
        <h2 v-show="!sidebarCollapsed" class="similar-music-page__sidebar-title">
          AI找音乐
        </h2>
        <button
          type="button"
          class="similar-music-page__toggle"
          :title="sidebarCollapsed ? '展开侧栏' : '收起侧栏'"
          @click="toggleSidebar"
        >
          <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" />
        </button>
      </div>
      <div v-show="!sidebarCollapsed" class="similar-music-page__sidebar-body">
        <HistoryPanel />
      </div>
    </aside>

    <main class="similar-music-page__main">
      <div class="similar-music-page__content">
        <MediaClipEditor
          v-if="stat"
          ref="clipEditor"
          :stat="stat"
          :wave-data="waveData"
          @search-clip="onSearchClip"
          @play-start="onClipPlayStart"
        />
        <div
          v-if="matchLoading || matchList !== null"
          class="similar-music-page__match"
          v-loading="matchLoading"
        >
          <h3 class="similar-music-page__match-title">相似结果</h3>
          <MatchResultList
            v-if="matchList !== null"
            ref="matchList"
            :list="matchList"
            :loading="matchLoading"
            @play-start="onMatchPlayStart"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import { getToken, getUserInfo } from '@/utils/author'
import { fetchSmlGet, fetchSmlMatch } from '@/api/similarAI'
import HistoryPanel from '../components/HistoryPanel.vue'
import MediaClipEditor from '../components/MediaClipEditor.vue'
import MatchResultList from '../components/MatchResultList.vue'

export default {
  name: 'SimilarMusicDetail',
  components: {
    HistoryPanel,
    MediaClipEditor,
    MatchResultList
  },
  data() {
    return {
      sidebarCollapsed: false,
      loading: false,
      stat: null,
      waveData: [],
      matchLoading: false,
      matchList: null
    }
  },
  created() {
    this.checkLogin()
  },
  mounted() {
    this.loadStat()
  },
  watch: {
    '$route.params.log_id'(val) {
      if (val) this.loadStat()
    }
  },
  methods: {
    checkLogin() {
      if (!getToken()) {
        this.$store.dispatch('user/openLogin')
      }
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    onClipPlayStart() {
      if (this.$refs.matchList) this.$refs.matchList.pauseAll()
    },
    onMatchPlayStart() {
      if (this.$refs.clipEditor) this.$refs.clipEditor.pause()
    },
    async loadStat() {
      const logId = this.$route.params.log_id
      if (!logId) {
        this.$message.error('缺少 log_id')
        return
      }
      this.loading = true
      this.stat = null
      this.waveData = []
      this.matchList = null
      try {
        const res = await fetchSmlGet({ sml_id: logId })
        const data = (res && res.data) || {}
        if (data.wave_path) {
          await this.loadWaveData(data.wave_path)
        }
        this.stat = data
      } catch (err) {
        console.error(err)
        this.$message.error((err && err.message) || '获取解析结果失败')
      } finally {
        this.loading = false
      }
    },
    async loadWaveData(wavePath) {
      try {
        const res = await axios.get(wavePath)
        const raw = res && res.data
        if (Array.isArray(raw)) {
          this.waveData = raw
        } else if (raw && Array.isArray(raw.data)) {
          this.waveData = raw.data
        } else {
          this.waveData = []
        }
      } catch (err) {
        console.error(err)
        this.waveData = []
      }
    },
    normalizeMatchList(payload) {
      if (Array.isArray(payload)) return payload
      if (payload && Array.isArray(payload.data)) return payload.data
      if (payload && Array.isArray(payload.list)) return payload.list
      return []
    },
    async onSearchClip({ range }) {
      if (!range || !/^\d+-\d+$/.test(range)) {
        this.$message.warning('时间范围格式不正确，应为 start-end')
        return
      }
      const parts = range.split('-').map(Number)
      if (parts.length !== 2 || parts[0] >= parts[1]) {
        this.$message.warning('时间范围无效')
        return
      }
      if (!getToken()) {
        this.$store.dispatch('user/openLogin')
        return
      }
      const smlId = (this.stat && this.stat.sml_id) || this.$route.params.log_id
      if (!smlId) {
        this.$message.error('缺少 sml_id')
        return
      }
      const userInfo = getUserInfo()
      const userId = userInfo && userInfo.user_id
      if (!userId) {
        this.$store.dispatch('user/openLogin')
        return
      }
      this.matchLoading = true
      this.matchList = null
      try {
        const res = await fetchSmlMatch({
          sml_id: smlId,
          user_id: userId,
          match_range: range
        })
        this.matchList = this.normalizeMatchList(res && res.data)
      } catch (err) {
        console.error(err)
        this.$message.error((err && err.message) || '匹配相似音乐失败')
        this.matchList = []
      } finally {
        this.matchLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.similar-music-page {
  display: flex;
  min-height: 100vh;
  padding-top: 60px;
  box-sizing: border-box;
  background: #f5f5f5;

  &__sidebar {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #ebebeb;
    background: #fff;
    transition: width 0.25s ease;

    &.is-collapsed {
      width: 48px;

      .similar-music-page__sidebar-header {
        padding: 0;
        justify-content: center;
      }

      .similar-music-page__toggle {
        margin-left: 0;
      }
    }
  }

  &__sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 12px 0 20px;
    box-sizing: border-box;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  &__sidebar-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    margin-left: auto;
    font-size: 18px;
    color: #666;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    outline: none;
    flex-shrink: 0;

    &:hover {
      color: #e52a0d;
      background: rgba(229, 42, 13, 0.06);
    }
  }

  &__sidebar-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__main {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 16px 50px 40px;
    box-sizing: border-box;
    min-width: 0;
  }

  &__content {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__match {
    width: 100%;
    min-height: 120px;
    background: #fff;
    border: 1px solid #ebebeb;
    border-radius: 8px;
    padding: 16px 20px;
    box-sizing: border-box;
  }

  &__match-title {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
  }
}
</style>
